import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import UserHelperService from '../../../../services/UserHelperService';
import { Fragment } from 'react';
import { FormControl, TextField, Autocomplete, Chip, CircularProgress, Tooltip, Grid } from '@mui/material';
import { Checkbox, ListItem, FormGroup, FormControlLabel } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import FieldNames from '../../../../defaults/FieldNames.json';
import { setFundingDepartment, setFundingAgency, setFundingRegion, 
  setFundingUnit, setFundingSubUnit, setFundingUnitType, setFundingUnitTypeIgnoreNational, setFundingTribe, setIgnoreContributedOnly } from '../../../../redux/slices/filterSlice';
import ErrorService from '../../../../services/ErrorService';

const BlueAlien = "#1976d2";

const useStyles = makeStyles({
  underline: {
    "&&&:before": {
      borderBottom: "2px solid red"
    },
    "&&:after": {
      borderBottom: "2px solid red"
    }
  },
  condensedFormControl: {
    margin: "5px 15px 5px 15px",
    minWidth: 120,
    width: '90%'
  },
  formControl: {
    margin: 15,
    minWidth: 120,
    width: '90%'
  },
  snackBarStyle: {
    top: '120px !important'
  },
});

const theme = createTheme({
  overrides: {
    MuiPaper: {
      root: {
        backgroundColor: 'green'
      }
    },
    MuiOutlinedInput: {
      root: {
        '& $notchedOutline': {
          //initial border
          borderColor: BlueAlien,
        },
        '&:hover $notchedOutline': {
          //border on hover
          borderColor: BlueAlien,
          border: '2px solid'
        },
        '&$focused $notchedOutline': {
          //border on focus
          borderColor: BlueAlien,
          border: '2px solid'
        },
      },
    }
  },
});

export default function AutoCompleteLinkedFilter({ userInfo, fundingDepartmentSelection, setFundingDepartmentSelection, fundingAgencySelection, setFundingAgencySelection,
  fundingRegionSelection, setFundingRegionSelection, fundingUnitSelection, setFundingUnitSelection, 
  fundingSubUnitSelection, setFundingSubUnitSelection, fundingUnitTypeSelection, setFundingUnitTypeSelection,
  fundingUnitTypeIgnoreNationalVal, setFundingUnitTypeIgnoreNationalVal, fundingTribeSelection, setFundingTribeSelection,
  ignoreContributedOnlyVal, setIgnoreContributedOnlyVal }) {
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const departmentLoading = departmentOpen && departmentOptions.length === 0;
  const [agencyOpen, setAgencyOpen] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState([]);
  const agencyLoading = agencyOpen && agencyOptions.length === 0;
  const [regionOpen, setRegionOpen] = useState(false);
  const [regionOptions, setRegionOptions] = useState([]);
  const regionLoading = regionOpen && regionOptions.length === 0;
  const [unitOpen, setUnitOpen] = useState(false);
  const [unitOptions, setUnitOptions] = useState([]);
  const unitLoading = unitOpen && unitOptions.length === 0;
  const [subUnitOpen, setSubUnitOpen] = useState(false);
  const [subUnitOptions, setSubUnitOptions] = useState([]);
  const [unitTypeOpen, setUnitTypeOpen] = useState(false);
  const [unitTypeOptions, setUnitTypeOptions] = useState([]);
  const unitTypeLoading = unitTypeOpen && unitTypeOptions.length === 0;

  const subUnitLoading = subUnitOpen && subUnitOptions.length === 0;
  const [tribeOpen, setTribeOpen] = useState(false);
  const [tribeOptions, setTribeOptions] = useState([]);
  const tribeLoading = tribeOpen && tribeOptions.length === 0;
  const config = useSelector((state) => state.configuration.config);
  const rootServiceUrl = useSelector((state) => state.configuration.rootServiceUrl);
  const nifcToken = useSelector((state) => state.configuration.nifcToken);

  
  const classes = useStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    let active = true;

    if (!departmentLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        UserHelperService.getAllDepartments().then(response => {
          setDepartmentOptions(response);
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    })();

    return () => {
      active = false;
    };
  }, [departmentLoading, config, rootServiceUrl]);

  useEffect(() => {
    let active = true;

    if (!agencyLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(fundingDepartmentSelection && fundingDepartmentSelection.length > 0){
          const arrayOfDept = fundingDepartmentSelection.map(x => x.Department);
          UserHelperService.getAgencyByDepartments(arrayOfDept).then(response => {
            setAgencyOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          UserHelperService.getAllAgencies().then(response => {
            setAgencyOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [agencyLoading, fundingDepartmentSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!agencyOpen) {
      setAgencyOptions([]);
    }
  }, [agencyOpen]);

  useEffect(() => {
    let active = true;

    if (!regionLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(fundingAgencySelection && fundingAgencySelection.length > 0){
          const arrayOfAgency = fundingAgencySelection.map(x => x.Agency);
          UserHelperService.getRegionsByAgencies(arrayOfAgency).then(response => {
            setRegionOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          UserHelperService.getAllRegions().then(response => {
            setRegionOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [regionLoading, fundingAgencySelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!regionOpen) {
      setRegionOptions([]);
    }
  }, [regionOpen]);

  useEffect(() => {
    let active = true;

    if (!unitLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(fundingRegionSelection && fundingRegionSelection.length > 0){
          const regionValues = fundingRegionSelection.map(x => x.Region);
          if(fundingAgencySelection)
          {
            const agencyValues = fundingAgencySelection.map(x => x.Agency);
            UserHelperService.getUnitsByRegionsAgencies(regionValues, agencyValues).then(response => {
              setUnitOptions(response);
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            });
          }
          else
          {
            UserHelperService.getUnitsByRegions(regionValues).then(response => {
              setUnitOptions(response);
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            });
          }
        } else {
          UserHelperService.getAllUnits().then(response => {
            setUnitOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [unitLoading, fundingRegionSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!unitOpen) {
      setUnitOptions([]);
    }
  }, [unitOpen]);

  useEffect(() => {
    let active = true;

    if (!subUnitLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(fundingUnitSelection && fundingUnitSelection.length > 0){
          const unitValues = fundingUnitSelection.map(x => x.Unit);
          UserHelperService.getSubUnitsByUnits(unitValues).then(response => {
            setSubUnitOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          UserHelperService.getAllSubUnits().then(response => {
            setSubUnitOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [subUnitLoading, fundingUnitSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!subUnitOpen) {
      setSubUnitOptions([]);
    }
  }, [subUnitOpen]);

  useEffect(() => {
    let active = true;

    if (!unitTypeLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        setUnitTypeOptions([]);
        UserHelperService.getAllUnitTypes().then(response => {
          setUnitTypeOptions(response);
        }).catch(error => {
          ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
        });
      }
    })();

    return () => {
      active = false;
    };
  }, [unitTypeLoading, config, rootServiceUrl]);

  useEffect(() => {
    if (!unitTypeOpen) {
      setUnitTypeOptions([]);
    }
  }, [unitTypeOpen]);


  useEffect(() => {
    let active = true;

    if (!tribeLoading) {
      return undefined;
    }

    (async () => {
      if (active) {
        if(fundingUnitSelection && fundingUnitSelection.length > 0){
          const unitValues = fundingUnitSelection.map(x => x.Unit);
          UserHelperService.getTribesByUnits(unitValues).then(response => {
            setTribeOptions(response);
          }).catch(error => {
            ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
          });
        } else {
          if(fundingRegionSelection && fundingRegionSelection.length > 0){
            const regionValues = fundingRegionSelection.map(x => x[FieldNames.units.region]);
            UserHelperService.getTribesByRegions(regionValues).then(response => {
              setTribeOptions(response);
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            });
          } else {
            UserHelperService.getAllTribes().then(response => {
              setTribeOptions(response);
            }).catch(error => {
              ErrorService.addErrorLogEntry(userInfo[FieldNames.userFields.nifcUsername], error);
            });
          }
          
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [tribeLoading, fundingTribeSelection, fundingRegionSelection, fundingUnitSelection, config, rootServiceUrl]);

  useEffect(() => {
    if (!tribeOpen) {
      setTribeOptions([]);
    }
  }, [tribeOpen]);


  return (
    <Grid container>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Department</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="departmentDemo"
                value={fundingDepartmentSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingDepartment(newValue));
                  setFundingDepartmentSelection(newValue);
                }}
                onOpen={() => {
                  setDepartmentOpen(true);
                }}
                onClose={() => {
                  setDepartmentOpen(false);
                }}
                isOptionEqualToValue={(option, value) => option.Department === value.Department}
                getOptionLabel={(option) => { 
                  return option.Department 
                }}
                options={departmentOptions}
                getOptionDisabled={(option) => {
                  if(fundingDepartmentSelection){
                    const selected = fundingDepartmentSelection.map(x => x.Department);
                    if(selected && option && selected.includes(option.Department)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={departmentLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.Department}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {departmentLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Agency</label>
              </span>
            </Grid>
            <Grid item size={6}>
            <Autocomplete
              multiple
                id="agencyDemo"
                value={fundingAgencySelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingAgency(newValue));
                  setFundingAgencySelection(newValue);
                }}
                onOpen={() => {
                  setAgencyOpen(true);
                }}
                onClose={() => {
                  setAgencyOpen(false);
                }}
                isOptionEqualToValue={(option, value) => option.Agency === value.Agency}
                getOptionLabel={(option) => { 
                  return option.Agency 
                }}
                options={agencyOptions}
                getOptionDisabled={(option) => {
                  if(fundingAgencySelection){
                    const selected = fundingAgencySelection.map(x => x.Agency);
                    if(selected && option && selected.includes(option.Agency)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={agencyLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.Agency}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {agencyLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Region</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="regionMulti"
                open={regionOpen}
                value={fundingRegionSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingRegion(newValue));
                  setFundingRegionSelection(newValue);
                }}
                onOpen={() => {
                  setRegionOpen(true);
                }}
                onClose={() => {
                  setRegionOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option.Region === value.Region }}
                getOptionLabel={(option) => { 
                  return option.Region 
                }}
                options={regionOptions}
                getOptionDisabled={(option) => {
                  if(fundingRegionSelection){
                    const selected = fundingRegionSelection.map(x => x.Region);
                    if(selected && option && selected.includes(option.Region)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={regionLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.Region}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {regionLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Unit</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="unitMulti"
                open={unitOpen}
                value={fundingUnitSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingUnit(newValue));
                  setFundingUnitSelection(newValue);
                }}
                onOpen={() => {
                  setUnitOpen(true);
                }}
                onClose={() => {
                  setUnitOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option.Unit === value.Unit }}
                getOptionLabel={(option) => { 
                  return option.Unit
                }}
                options={unitOptions}
                getOptionDisabled={(option) => {
                  if(fundingUnitSelection){
                    const selected = fundingUnitSelection.map(x => x.Unit);
                    if(selected && option && selected.includes(option.Unit)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={unitLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.Unit}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding SubUnit</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="unitMulti"
                open={subUnitOpen}
                value={fundingSubUnitSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingSubUnit(newValue));
                  setFundingSubUnitSelection(newValue);
                }}
                onOpen={() => {
                  setSubUnitOpen(true);
                }}
                onClose={() => {
                  setSubUnitOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option.SubUnit === value.SubUnit }}
                getOptionLabel={(option) => { 
                  return option.SubUnit
                }}
                options={subUnitOptions}
                getOptionDisabled={(option) => {
                  if(fundingSubUnitSelection){
                    const selected = fundingSubUnitSelection.map(x => x.SubUnit);
                    if(selected && option && selected.includes(option.SubUnit)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={subUnitLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.SubUnit}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12}  container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Tribe</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="tribeMulti"
                open={tribeOpen}
                value={fundingTribeSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingTribe(newValue));
                  setFundingTribeSelection(newValue);
                }}
                onOpen={() => {
                  setTribeOpen(true);
                }}
                onClose={() => {
                  setTribeOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option[FieldNames.units.tribeFullName] === value[FieldNames.units.tribeFullName] }}
                getOptionLabel={(option) => { 
                  return option[FieldNames.units.tribeFullName]
                }}
                options={tribeOptions}
                getOptionDisabled={(option) => {
                  if(fundingTribeSelection){
                    const selected = fundingTribeSelection.map(x => x[FieldNames.units.tribeFullName]);
                    if(selected && option && selected.includes(option[FieldNames.units.tribeFullName])){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={tribeLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option[FieldNames.units.tribeFullName]}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <Grid item size={12} container direction="row" className="grid-item" style={{ alignItems: 'center' }}>
            <Grid item size={6}>
              <span>
                <label>Funding Unit Type</label>
              </span>
            </Grid>
            <Grid item size={6}>
              <Autocomplete
                multiple
                id="unitMulti"
                open={unitTypeOpen}
                value={fundingUnitTypeSelection}
                disableClearable
                onChange={(event, newValue) => {
                  dispatch(setFundingUnitType(newValue));
                  setFundingUnitTypeSelection(newValue);
                }}
                onOpen={() => {
                  setUnitTypeOpen(true);
                }}
                onClose={() => {
                  setUnitTypeOpen(false);
                }}
                isOptionEqualToValue={(option, value) => { return option.UnitType === value.UnitType }}
                getOptionLabel={(option) => { 
                  return option.UnitType
                }}
                options={unitTypeOptions}
                getOptionDisabled={(option) => {
                  if(fundingUnitTypeSelection){
                    const selected = fundingUnitTypeSelection.map(x => x.UnitType);
                    if(selected && option && selected.includes(option.UnitType)){
                      return true;
                    } else {
                      return false;
                    }
                  } else {
                    return false;
                  }
                }}
                loading={unitTypeLoading}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip
                      label={option.UnitType}
                      {...getTagProps({ index })}
                      disabled={false}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {unitLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params?.InputProps?.endAdornment}
                          </React.Fragment>
                        )
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <ListItem style={{ margin: 0, padding: 0 }}>
            <FormGroup className={classes.filterItemSelection} >
              <FormControlLabel style={{ margin: '2px 0 2px 0' }} control={
                <Checkbox 
                  style={{ padding: '0', margin: '0 5px 0 0px', borderColor: BlueAlien }} 
                  id="hideNational" 
                  checked={fundingUnitTypeIgnoreNationalVal ? true : false}
                  onClick={(event) => {
                    dispatch(setFundingUnitTypeIgnoreNational(!fundingUnitTypeIgnoreNationalVal));
                    setFundingUnitTypeIgnoreNationalVal(!fundingUnitTypeIgnoreNationalVal);
                  }}
                />
              } label="Hide National" />
            </FormGroup>
          </ListItem>
        </ThemeProvider>
      </FormControl>
      <FormControl variant="standard" className={classes.formControl}>
        <ThemeProvider theme={theme}>
          <ListItem style={{ margin: 0, padding: 0 }}>
            <FormGroup className={classes.filterItemSelection} >
              <Tooltip title={'Selecting this will exclude any treatments or activities that ONLY have contributed funding lines of accounting.'}>
                <FormControlLabel style={{ margin: '2px 0 2px 0' }} control={
                  <Checkbox 
                    style={{ padding: '0', margin: '0 5px 0 0px', borderColor: BlueAlien }} 
                    id="hideContributedOnly" 
                    checked={ignoreContributedOnlyVal ? true : false}
                    onClick={(event) => {
                      dispatch(setIgnoreContributedOnly(!ignoreContributedOnlyVal));
                      setIgnoreContributedOnlyVal(!ignoreContributedOnlyVal);
                    }}
                  />
              } label="Hide Contributed Only" />
              </Tooltip>
            </FormGroup>
          </ListItem>
        </ThemeProvider>
      </FormControl>
    </Grid>
  );
}